import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getClientId,
  getDistributionAggregatePresenceRest,
  type DistributionAggregatePresencePoint,
} from "../services/distributionService";
import { resolveWebSocketGatewayOrigin } from "../runtime/loadSharedDashboardConfig";

function buildDistributionAggregatePresenceWsUrl(params: {
  clientId: string;
  type: string;
  from?: string;
  to?: string;
  locationId: string;
  cameraId: string;
}): string {
  const wsOrigin = resolveWebSocketGatewayOrigin();
  const u = new URL(`${wsOrigin}/analytic/distribution-aggregate-presence`);
  u.searchParams.set("clientId", params.clientId);
  u.searchParams.set("type", params.type);
  if (params.from) u.searchParams.set("from", params.from);
  if (params.to) u.searchParams.set("to", params.to);
  u.searchParams.set("locationId", params.locationId);
  u.searchParams.set("cameraId", params.cameraId);
  return u.toString();
}

type WsMessage =
  | { type: "connected"; client_id?: string; message?: string }
  | {
      type: "aggregate_presence";
      stream_type?: string;
      data: DistributionAggregatePresencePoint[] | string;
      timestamp?: string;
    };

function disposeWebSocket(socket: WebSocket | null) {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  const { readyState } = socket;
  if (readyState === WebSocket.CONNECTING) {
    socket.addEventListener(
      "open",
      () => {
        try {
          socket.close();
        } catch {
          // ignore
        }
      },
      { once: true }
    );
  } else if (readyState === WebSocket.OPEN) {
    try {
      socket.close();
    } catch {
      // ignore
    }
  }
}

function normalizeAggregatePayload(raw: unknown): DistributionAggregatePresencePoint[] {
  const parsed = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((item): item is [string, number] => Array.isArray(item) && item.length >= 2)
    .map(([time, value]) => [String(time), Number(value)]);
}

function safeJsonParse(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    return [];
  }
}

export function useDistributionAggregatePresence(params: {
  from?: string;
  to?: string;
  location_id?: string;
  camera_id?: string;
  type: string;
  enabled?: boolean;
}) {
  const { enabled = true, type, from, to, location_id, camera_id } = params;
  const clientId = getClientId();
  const queryEnabled = enabled && !!clientId;

  const restQuery = useQuery({
    queryKey: ["distribution-aggregate-presence", "rest", type, from, to, location_id, camera_id],
    queryFn: () =>
      getDistributionAggregatePresenceRest({
        type,
        from,
        to,
        location_id,
        camera_id,
      }),
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    retry: 1,
    retryDelay: 2000,
  });

  const [wsData, setWsData] = useState<DistributionAggregatePresencePoint[] | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  const loc = typeof location_id === "string" ? location_id : "";
  const cam = typeof camera_id === "string" ? camera_id : "";

  const wsKey = useMemo(
    () => `${type}|${from ?? ""}|${to ?? ""}|${loc}|${cam}`,
    [type, from, to, loc, cam]
  );

  useEffect(() => {
    if (!queryEnabled || !clientId) {
      setWsData(null);
      setWsConnected(false);
      return;
    }

    setWsData(null);
    setWsConnected(false);

    const url = buildDistributionAggregatePresenceWsUrl({
      clientId,
      type,
      from,
      to,
      locationId: loc,
      cameraId: cam,
    });

    let cancelled = false;
    let ws: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }

      ws.onmessage = (event) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(event.data as string) as WsMessage;
          if (msg.type === "connected") {
            setWsConnected(true);
            return;
          }
          if (msg.type === "aggregate_presence") {
            if (msg.stream_type && msg.stream_type !== type) return;
            setWsData(normalizeAggregatePayload(msg.data));
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onerror = () => {
        // keep REST data
      };

      ws.onclose = () => {
        if (cancelled) return;
        setWsConnected(false);
        retryTimer = setTimeout(() => {
          if (!cancelled) connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer != null) clearTimeout(retryTimer);
      disposeWebSocket(ws);
      ws = null;
    };
  }, [queryEnabled, clientId, wsKey, type, from, to, loc, cam]);

  const restData = restQuery.data ?? [];
  const data = wsConnected && wsData != null ? wsData : restData;
  const isLoading = restQuery.isLoading && wsData == null;
  const isError = restQuery.isError && wsData == null;

  return {
    data,
    isLoading,
    isError,
    wsConnected,
    usingWebSocketData: wsConnected && wsData != null,
    refetch: restQuery.refetch,
  };
}
